/** @file Checks a plain object against a shape: no unknown keys, required keys present, each field checked. */
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import type { Shape } from '@zilo/pose3d-format/validate/fields/fieldCheck';

/** Hints for fields that older drafts used (design 15.1, 15.4, 17.1, 17.7, 18.6, ruling R1). */
const RENAMED: Record<string, string> = {
  zFactor: 'zFactor was removed (design 17.7): use zScaleRotate (rotation space) and zScaleView (display ratio)',
  zScaleOverride: 'zScaleOverride was replaced (design 17.7) by zScaleRotate and zScaleView',
  refAngles: 'refAngles was split (design 15.4): drawnAzimuths per tile, plus drawnScreenAngles for rotate screen/parent',
  pivot: 'impostor pivots moved into each facet entry as pivotFrac, a fraction of the tile size (design 17.1)',
  rotate: 'rotate moved into each facet entry (design 17.1)',
  cut: 'cut moved into each facet entry (design 17.1)',
  cutInverse: 'cutInverse moved into each facet entry (design 17.1)',
  pitch: 'a facet entry lists its pitch ranges as bands (ruling R1), e.g. bands: ["ALL"]',
  pixelOffset: 'pixelOffset was replaced by originOffset in units, added to the bone origin and the node position (design 18.6)',
  ortho: 'camera.ortho was replaced by camera.kind: "ortho" (design 15.11)',
  socket: 'an attachment names its host nodes through nodeMap, when { hostNode, parent } and driver (ruling R1)',
  showWhenParent: 'showWhenParent was replaced by when: { hostNode, parent } (ruling R1)',
  puppet: 'an attachment names its source puppet as source (ruling R1)',
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function rejectUnknown(value: Record<string, unknown>, path: string, shape: Shape): void {
  for (const key of Object.keys(value)) {
    if (!Object.hasOwn(shape.required, key) && !Object.hasOwn(shape.optional ?? {}, key)) {
      failField(`${path}/${key}`, 'no such field', value[key], Object.hasOwn(RENAMED, key) ? RENAMED[key] : undefined);
    }
  }
}

/** Returns `value` as a record after checking it against `shape`; throws `VALIDATION` with the field's path. */
export function expectShape(value: unknown, path: string, shape: Shape): Record<string, unknown> {
  if (!isPlainObject(value)) return failField(path, 'object', value);
  rejectUnknown(value, path, shape);
  for (const [key, check] of Object.entries(shape.required)) {
    if (value[key] === undefined) failField(`${path}/${key}`, 'required field', undefined);
    check(value[key], `${path}/${key}`);
  }
  for (const [key, check] of Object.entries(shape.optional ?? {})) {
    if (value[key] !== undefined) check(value[key], `${path}/${key}`);
  }
  return value;
}
